<script setup lang="ts">
import CompactTag from './LegacyCompactTag.vue';
import {CurrencyDisplay, formatting, LegacyOrder} from '@hantera/portal-app';
import {computed} from 'vue';

const props = defineProps<{
  order: LegacyOrder
  onClick?: any // Used to check if this is clickable or not
}>();

const emit = defineEmits<{
  (e: 'click', order: LegacyOrder): void
}>();

const { date, currency } = formatting;

const clickable = computed(() => {
  return typeof props.onClick === 'function';
});

function click() {
  emit('click', props.order);
}

</script>

<template>
  <div
    class="compact-order"
    :class="{
      'compact-order--clickable': clickable
    }"
    @click="clickable && click()"
  >
    <div
      :class="`compact-order__state`"
      :style="`background-color: var(--state-colors__order-${order.state.toLowerCase()})`"
      :title="order.state"
    ></div>

    <div
      class="compact-order__info"
      :class="{
        'compact-order__info--has-tags': order.tags?.length > 0
      }"
    >
      <div class="compact-order__order-number">
        {{ order.orderNumber }}
      </div>
      <div class="compact-order__order-date">
        {{ date(order.createdUtc) }}
      </div>

      <div
        class="compact-order__order-tags"
        v-if="order.tags?.length > 0"
      >
        <CompactTag
          v-for="tag in order.tags"
          :key="tag.tagName"
          :tag="tag"
        />
      </div>
      <div class="compact-order__order-total">
        {{ currency(order.grandTotal, order.currencyCode, CurrencyDisplay.code) }}
      </div>
    </div>

  </div>
</template>

<style scoped>
.compact-order {
  border-bottom: 1px solid hsl(var(--color-70));
  position: relative;
  padding: 5px 5px 5px 15px;
}

.compact-order:last-child {
  border-bottom: none;
}

.compact-order__state {
  position: absolute;
  left: 5px;
  top: 5px;
  bottom: 5px;
  width: 5px;
  border-radius: 5px
}

.compact-order__info {
  display: grid;
  grid-template-columns: max-content auto max-content;
  gap: 5px;
}

.compact-order__order-date {
  padding-left: 5px;
  opacity: .6;
}

.compact-order__order-tags {
  grid-row: 2 / 3;
  grid-column: 1 / 3;
  display: flex;
  overflow: hidden;
}

.compact-order__info--has-tags .compact-order__order-total {
  grid-row: 1 / 3;
}

.compact-order__order-total {
  grid-column: 3 / 4;
  grid-row: 1 / 2;
  display: flex;
  align-items: center;
}

.compact-order--clickable {
  cursor: pointer;
}

.compact-order--clickable:hover {
  background-color: hsl(var(--color-80));
}
</style>